import React, { useEffect } from 'react';
import { useDispatch, useSelector } from "react-redux";
import PropTypes from 'prop-types';

import DeleteIcon from '@mui/icons-material/Delete';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

import {dataRemoveAllFilters, dataRemoveFilter} from "../../redux/data/dataActions";

import {
    styled,
    lighten,
    Table,
    TableBody,
    TableCell,
    Paper,
    Tooltip,
    LinearProgress,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    TableSortLabel,
    Toolbar,
    Typography,
    IconButton,
} from '@mui/material';

function createData(id, field, alias, condition, showClear, showBorderBottom) {
    return { id, field, alias, condition, showClear, showBorderBottom };
}

function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
        return -1;
    }
    if (b[orderBy] > a[orderBy]) {
        return 1;
    }
    return 0;
}

function getComparator(order, orderBy) {
    return order === 'desc'
        ? (a, b) => descendingComparator(a, b, orderBy)
        : (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort(array, comparator) {
    const stabilizedThis = array.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
        const order = comparator(a[0], b[0]);
        if (order !== 0) return order;
        return a[1] - b[1];
    });
    return stabilizedThis.map(el => el[0]);
}

const headCells = [
    { id: 'field', numeric: false, disablePadding: true, label: 'Data Field' },
    { id: 'condition', numeric: false, disablePadding: true, label: 'Condition' }
];

const PREFIX = 'TableHead';

const classes = {
    root: `${PREFIX}-root`,
};

const StyledTableHead = styled(TableHead)((
    {
        theme
    }
) => ({
    [`&.${classes.root}`]: {
        minWidth: '500px'
    }
}));

function EnhancedTableHead(props) {
    const { order, orderBy, onRequestSort } = props;
    // const classes = useTableHeadStyles();

    const createSortHandler = property => event => {
        onRequestSort(event, property);
    };

    return (
        <StyledTableHead className={classes.root}>
            <TableRow>
                <TableCell padding="checkbox">
                    <div></div>
                </TableCell>
                {headCells.map(headCell => (
                    <TableCell
                        key={headCell.id}
                        align={headCell.numeric ? 'right' : 'left'}
                        padding={headCell.disablePadding ? 'none' : 'default'}
                        sortDirection={orderBy === headCell.id ? order : false}
                    >
                        <TableSortLabel
                            active={orderBy === headCell.id}
                            direction={orderBy === headCell.id ? order : 'asc'}
                            onClick={createSortHandler(headCell.id)}
                        >
                            {headCell.label}
                        </TableSortLabel>
                    </TableCell>
                ))}
            </TableRow>
        </StyledTableHead>
    );
}

EnhancedTableHead.propTypes = {
    classes: PropTypes.object.isRequired,
    // numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    // onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};

const PREFIX2 = 'EnhancedTableToolbar';

const classes2 = {
    root: `${PREFIX2}-root`,
    // highlight: `${PREFIX2}-highlight`,
    title: `${PREFIX2}-title`
};

const StyledEnhancedTableToolbar = styled(Toolbar)((
    {
        theme
    }
) => ({
    [`&.${classes2.root}`]: {
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(1),
        minWidth: '500px'
    },

    // [`& .${classes2.highlight}`]: {
    // 	theme.palette.mode === 'light'
    // 		? {
    // 			color: theme.palette.secondary.main,
    // 			backgroundColor: lighten(theme.palette.secondary.light, 0.85),
    // 		}
    // 		: {
    // 			color: theme.palette.text.primary,
    // 			backgroundColor: theme.palette.secondary.dark,
    // 		}
    // },

    [`& .${classes2.title}`]: {
        flex: '1 1 100%'
    }
}));

const EnhancedTableToolbar = props => {
    const dispatch = useDispatch();

    const clearAllFilters = () => {
        dispatch(dataRemoveAllFilters());
    };

    return (
        <React.Fragment>
            <StyledEnhancedTableToolbar className={classes2.root}>
                <Typography className={classes2.title} variant="h6" id="tableTitle">
                    Filters Applied
                </Typography>
                <Tooltip title="Clear all filters">
                    <IconButton aria-label="delete" onClick={clearAllFilters} size="large">
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </StyledEnhancedTableToolbar>
        </React.Fragment>
    );
};

const PREFIX3 = 'EnhancedTableDiv';

const classes3 = {
    root: `${PREFIX3}-root`,
    title: `${PREFIX3}-paper`,
    linearProgress: `${PREFIX3}-linearProgress`,
    tableContainer: `${PREFIX3}-tableContainer`,
    table: `${PREFIX3}-table`,
    visuallyHidden: `${PREFIX3}-visuallyHidden`
};

const StyledEnhancedTableDiv = styled('div')((
    {
        theme
    }
) => ({
    [`&.${classes3.root}`]: {
        width: '100%',
        height: 'calc(100% - 1px)',
    },

    [`& .${classes3.paper}`]: {
        height: '100%',
        width: '100%',
        boxShadow: 'none'
    },

    [`& .${classes3.linearProgress}`]: {
        height: '1px'
    },

    [`& .${classes3.tableContainer}`]: {
        height: 'calc(100% - 116px)',
        width: '100%',
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
    },

    [`& .${classes3.table}`]: {
        minWidth: 200,
        width: '100%'
    },

    [`& .${classes3.visuallyHidden}`]: {
        border: 0,
        clip: 'rect(0 0 0 0)',
        height: 1,
        margin: -1,
        overflow: 'hidden',
        padding: 0,
        position: 'absolute',
        top: 20,
        width: 1,
    },

}));

export default function EnhancedTable({filters, fields, aliases}) {

    const dispatch = useDispatch();

    // Component State
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('field');
    // const [selected, setSelected] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [rows, setRows] = React.useState([]);

    // App State
    // const filters = useSelector(state => state.data.filters);
// console.log(filters);
    // Load up roles
    useEffect(() => {
        const newFilterRows = [];

        if (filters) {
            for (const filter of filters) {
                if (filter.id !== "-2112" && !filter.hidden) {

                    for (let c = 0; c < filter.conditions.length; c++) {
                        const condition = filter.conditions[c];

                        // Lookup the alias
                        const lu = fields.indexOf(filter.field);
                        let alias = "";
                        if (lu >= 0)
                            alias = aliases[lu];

                        if (alias && alias.trim() !== "") {
                            alias = "{0}".format(aliases[lu]);
                        } else {
                            alias = "No Alias ({0})".format(filter.field);
                        }

                        if (c === 0) {
                            newFilterRows.push(createData(filter.id, filter.field, alias, condition, true, filter.conditions.length === 1 ? true : false));
                        } else {
                            newFilterRows.push(createData(filter.id, filter.field, alias, condition, false, true));
                        }
                    }
                }
            }
        }

        setRows(newFilterRows);
    }, [filters]);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const clearFilter = (id) => {
        setRows([]);
        dispatch(dataRemoveFilter(id));
    };

    const emptyRows = rowsPerPage - Math.min(rowsPerPage, rows.length - page * rowsPerPage);

    return (
        <StyledEnhancedTableDiv className={classes3.root}>
            <Paper className={classes3.paper}>

                {!rows &&
                    <LinearProgress className={classes3.linearProgress} />
                }

                <EnhancedTableToolbar />

                <TableContainer className={classes3.tableContainer}>
                    <Table
                        className={classes3.table}
                        aria-labelledby="tableTitle"
                        size={'medium'}
                        aria-label="enhanced table"
                    >
                        <EnhancedTableHead
                            classes={classes}
                            // numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {rows &&
                            <React.Fragment>
                                {stableSort(rows, getComparator(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((row, index) => {

                                        const noBorderStyle = {};
                                        if (!row.showBorderBottom) {
                                            noBorderStyle.borderBottom = "none";
                                        }

                                        return (
                                            <TableRow
                                                hover
                                                tabIndex={-1}
                                                key={row.id}
                                                style={noBorderStyle}
                                            >
                                                <TableCell padding="checkbox" style={noBorderStyle}>
                                                    {row.showClear &&
                                                    <Tooltip title="Clear filter">
                                                        <IconButton aria-label="delete" onClick={() => clearFilter(row.id)} size="large">
                                                            <HighlightOffIcon/>
                                                        </IconButton>
                                                    </Tooltip>
                                                    }
                                                    {!row.showClear &&
                                                        <div style={{height: "48px"}}/>
                                                    }
                                                </TableCell>
                                                <TableCell component="th" id={row.id} scope="row" padding="none"
                                                           style={noBorderStyle}>
                                                    {row.alias}
                                                </TableCell>
                                                <TableCell align="left" padding="none"
                                                           style={noBorderStyle}>{row.condition}</TableCell>
                                            </TableRow>
                                        );
                                    })}
                                {emptyRows > 0 && (
                                    <TableRow style={{height: 53 * emptyRows}}>
                                        <TableCell colSpan={6}/>
                                    </TableRow>
                                )}
                            </React.Fragment>
                            }
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 15]}
                    className={classes3.table}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </StyledEnhancedTableDiv>
    );
}
